import { useState } from "react"
import { useNavigate } from "react-router-dom"

import { useGetIntegrationDetails } from "@/api/useIntegrations"
import { Button, Flex } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { Protected } from "@/molecules/Protected"
import { ReadInstructionButton } from "@/molecules/ReadInstructionButton"
import { INTEGRATIONS_WRITE_PERMISSION } from "@/pages/IntegrationsPage/IntegrationsPage.constants"
import { AboutSection } from "@/pages/IntegrationsPage/components/molecules/AboutSection"
import { ConnectButton } from "@/pages/IntegrationsPage/components/molecules/ConnectButton"
import { ConnectMethodSteps } from "@/pages/IntegrationsPage/components/molecules/ConnectMethodSteps"
import { PrerequisitesSection } from "@/pages/IntegrationsPage/components/molecules/PrerequisitesSection"
import { LinkIcon } from "@/pages/IntegrationsPage/components/organisms/ItemIntegration/LinkIcon"
import { useAsanaSignIn } from "@/pages/IntegrationsPage/hooks/useAsanaSignIn"

import type { AsanaConnectMethod } from "@/pages/IntegrationsPage/components/pages/AsanaIntegrationPage"

const METHODS: AsanaConnectMethod[] = ["oauth", "token"]

export const AsanaConnectTab = () => {
  const vendor = INTEGRATION_VENDORS.asana
  const { t } = useTranslation("translation", {
    keyPrefix: "integrations.asana"
  })
  const { logger, EVENTS } = useLogger()
  const navigate = useNavigate()
  const asanaSignIn = useAsanaSignIn()
  const { response } = useGetIntegrationDetails(vendor)
  const [firstSection, secondSection] =
    response?.data?.integration?.connectPage?.sections || []
  const [method, setMethod] = useState<AsanaConnectMethod>("oauth")

  const onConnect = () => {
    logger.info(EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.OPENED_CONNECT, {
      name: vendor,
      method
    })
    if (method === "oauth") asanaSignIn.signIn()
    else navigate(`/${NAMES_ROUTES.INTEGRATIONS.ROOT}/${vendor}/connect`)
  }

  return (
    <>
      {firstSection && <AboutSection section={firstSection} hasDivider />}
      {secondSection && <PrerequisitesSection section={secondSection} />}
      {secondSection?.instructionsLink && (
        <Flex justify="flex-end">
          <ReadInstructionButton href={secondSection.instructionsLink} />
        </Flex>
      )}
      <ConnectMethodSteps<AsanaConnectMethod>
        selectTitle={t("method.title")}
        value={method}
        options={METHODS.map((value) => ({
          value,
          label: t(`method.${value}`),
          description: t(`method.${value}Description`)
        }))}
        onChange={setMethod}
        actionTitle={t("connect.title")}
        action={
          method === "oauth" ? (
            <Protected permission={INTEGRATIONS_WRITE_PERMISSION} shouldDisable>
              <Button
                icon={<LinkIcon type="externalLink" />}
                type="primary"
                loading={asanaSignIn.isPending}
                onClick={onConnect}
              >
                {t("connect.oauth")}
              </Button>
            </Protected>
          ) : (
            <ConnectButton onClick={onConnect}>
              {t("connect.token")}
            </ConnectButton>
          )
        }
        helper={t(`connect.helper.${method}`)}
      />
    </>
  )
}
